import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import AssignmentChip from '@/components/schedule/AssignmentChip';
import {
  buildWeekGrid,
  groupByDate,
  isoDate,
  sameIsoDate,
  formatDayShort,
} from '@/lib/scheduleData';

export const CalendarWeek = ({
  cursor,
  assignments = [],
  jobs = [],
  onPickDate,
  onPickAssignment,
}) => {
  const { t } = useTranslation();
  const days = useMemo(() => buildWeekGrid(cursor), [cursor]);
  const groups = useMemo(() => groupByDate(assignments), [assignments]);
  const today = new Date();

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
      <CardContent className="p-3 sm:p-4">
        <div
          className="grid grid-cols-1 sm:grid-cols-7 gap-2"
          data-testid="calendar-week-grid"
        >
          {days.map((d) => {
            const isToday = sameIsoDate(d, today);
            const key = isoDate(d);
            const list = groups.get(key) || [];
            return (
              <div
                role="button"
                tabIndex={0}
                key={key}
                onClick={() => onPickDate?.(d)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onPickDate?.(d);
                  }
                }}
                data-testid={`calendar-day-${key}`}
                data-today={isToday ? 'true' : undefined}
                className={cn(
                  'rounded-md border bg-[hsl(var(--background))]/40 cursor-pointer text-left p-2.5 min-h-[140px] hover:border-[hsl(var(--primary))]/30 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40',
                  isToday
                    ? 'border-[hsl(var(--primary))]/60'
                    : 'border-border'
                )}
              >
                <div className="flex items-center justify-between">
                  <div
                    className={cn(
                      'text-xs font-semibold uppercase tracking-[0.16em]',
                      isToday ? 'text-[hsl(var(--primary))]' : 'text-foreground'
                    )}
                  >
                    {formatDayShort(d)}
                  </div>
                  {isToday ? (
                    <span className="text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                      {t('schedule.todayBadge')}
                    </span>
                  ) : null}
                </div>
                <div className="mt-2 space-y-1.5">
                  {list.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground">
                      {t('schedule.noAssignments')}
                    </div>
                  ) : (
                    list.map((a) => (
                      <AssignmentChip
                        key={a.id}
                        assignment={a}
                        jobs={jobs}
                        onClick={onPickAssignment}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default CalendarWeek;
